import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Crown, MagnifyingGlass, BookOpen, ArrowRight, PencilSimple, Trash, Plus, WarningCircle } from '@phosphor-icons/react';

export default function MandantesPage() {
  const navigate = useNavigate();
  const [mandantes, setMandantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', trade_name: '', rut: '', address: '', email: '', phone: '' });
  const [deleting, setDeleting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [hasLogo, setHasLogo] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState('');

  const fetchMandantes = async () => {
    try {
      const res = await companiesApi.getAll({ is_client: true, page_size: 999 });
      setMandantes(res.data.items || res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchMandantes(); }, []);

  const filtered = mandantes.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.trade_name && m.trade_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openEdit = (m, e) => {
    e.stopPropagation();
    setFormData({ name: m.name, trade_name: m.trade_name || '', rut: m.rut || '', address: m.address || '', email: m.email || '', phone: m.phone || '' });
    setHasLogo(!!m.has_logo);
    setLogoVersion(0);
    setPendingLogoFile(null);
    setPendingLogoPreview('');
    setEditDialog(m);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes (PNG, JPG)'); return; }
    // En modo creación guardamos el archivo para subirlo tras crear el mandante
    if (!editDialog) {
      setPendingLogoFile(file);
      setPendingLogoPreview(URL.createObjectURL(file));
      return;
    }
    setUploadingLogo(true);
    try {
      await companiesApi.uploadLogo(editDialog.id, file);
      toast.success('Logo subido');
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
      fetchMandantes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir el logo'); }
    finally { setUploadingLogo(false); }
  };

  const handleLogoDelete = async () => {
    if (!editDialog) {
      setPendingLogoFile(null);
      setPendingLogoPreview('');
      return;
    }
    try {
      await companiesApi.deleteLogo(editDialog.id);
      toast.success('Logo eliminado');
      setHasLogo(false);
      setLogoVersion((v) => v + 1);
      fetchMandantes();
    } catch (err) { toast.error('Error al eliminar el logo'); }
  };

  const handleSave = async () => {
    const clean = (obj) => {
      const out = { ...obj };
      ['trade_name', 'rut', 'address', 'phone', 'email'].forEach((k) => {
        if (!out[k] || !String(out[k]).trim()) out[k] = null;
      });
      return out;
    };
    const errMsg = (err) => {
      const d = err.response?.data?.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) return d.map((x) => x.msg || '').join(', ') || 'Error de validación';
      return 'Error';
    };
    try {
      if (editDialog) {
        await companiesApi.update(editDialog.id, clean(formData));
        toast.success('Mandante actualizado');
        setEditDialog(null);
      } else {
        const res = await companiesApi.create({ ...clean(formData), is_client: true });
        const newId = res.data?.id;
        if (pendingLogoFile && newId) {
          try { await companiesApi.uploadLogo(newId, pendingLogoFile); }
          catch { toast.error('Mandante creado, pero falló la carga del logo'); }
        }
        toast.success('Mandante creado');
        setCreateDialog(false);
        setPendingLogoFile(null);
        setPendingLogoPreview('');
      }
      fetchMandantes();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await companiesApi.deleteCascade(deleteDialog.id);
      toast.success('Mandante y sus datos eliminados');
      setDeleteDialog(null);
      fetchMandantes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
    finally { setDeleting(false); }
  };

  const openCreate = () => {
    setFormData({ name: '', trade_name: '', rut: '', address: '', email: '', phone: '' });
    setPendingLogoFile(null);
    setPendingLogoPreview('');
    setCreateDialog(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="mandantes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Mandantes</h1>
          <p className="text-sm text-[#2B4B8C]/50 mt-1">Clientes y sus cursos configurados</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2BBAA4] hover:bg-[#2BBAA4]/90 text-white" data-testid="create-mandante-btn">
          <Plus size={16} className="mr-2" /> Nuevo Mandante
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/40" />
        <Input placeholder="Buscar mandante..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="mandantes-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <Card key={m.id} className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/40 hover:shadow-md transition-all duration-200 cursor-pointer group overflow-hidden" onClick={() => navigate(`/admin/mandantes/${m.id}`)} data-testid={`mandante-card-${m.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Crown size={20} weight="fill" className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="font-semibold text-[#2B4B8C] text-sm truncate">{m.name}</h3>
                  {m.trade_name && <p className="text-xs text-[#2B4B8C]/50 truncate">{m.trade_name}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => openEdit(m, e)} className="p-1.5 rounded-md hover:bg-[#2B4B8C]/5 text-[#2B4B8C]/40 hover:text-[#2B4B8C]" data-testid={`edit-mandante-${m.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteDialog(m); }} className="p-1.5 rounded-md hover:bg-red-50 text-[#2B4B8C]/40 hover:text-red-500" data-testid={`delete-mandante-${m.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-[#2B4B8C]/40">
                <BookOpen size={14} />
                <span>Ver cursos configurados</span>
              </div>
              {m.rut && <p className="mt-1 text-[10px] text-[#2B4B8C]/30 font-mono truncate">{m.rut}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && <div className="text-center py-12 text-[#2B4B8C]/40">No se encontraron mandantes</div>}

      {/* Edit / Create Dialog */}
      <Dialog open={!!editDialog || createDialog} onOpenChange={() => { setEditDialog(null); setCreateDialog(false); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">{editDialog ? 'Editar Mandante' : 'Nuevo Mandante'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="mandante-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Nombre Fantasía</Label>
                <Input value={formData.trade_name} onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">RUT</Label>
                <Input value={formData.rut} onChange={(e) => setFormData({ ...formData, rut: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Dirección</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
            </div>
            {editDialog && (
              <div className="space-y-2 border-t border-[#2B4B8C]/10 pt-4">
                <Label className="text-[#2B4B8C]/80">Logo del mandante</Label>
                <p className="text-xs text-[#2B4B8C]/50">Se mostrará en la esquina superior derecha de los certificados. PNG o JPG, máx 5 MB.</p>
                <div className="flex items-center gap-4">
                  <div className="w-36 h-20 rounded-lg border border-dashed border-[#2B4B8C]/20 bg-[#f8fafc] flex items-center justify-center overflow-hidden">
                    {hasLogo ? (
                      <img
                        src={`${companiesApi.logoUrl(editDialog.id)}?v=${logoVersion}`}
                        alt="Logo mandante"
                        className="max-w-full max-h-full object-contain"
                        data-testid="mandante-logo-preview"
                      />
                    ) : (
                      <span className="text-xs text-[#2B4B8C]/30">Sin logo</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      id="mandante-logo-file"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = ''; }}
                      data-testid="mandante-logo-input"
                    />
                    <Button
                      type="button" variant="outline"
                      onClick={() => document.getElementById('mandante-logo-file').click()}
                      disabled={uploadingLogo}
                      className="border-[#2B4B8C]/15 text-[#2B4B8C]"
                      data-testid="mandante-logo-upload-btn"
                    >
                      {uploadingLogo ? 'Subiendo...' : (hasLogo ? 'Cambiar logo' : 'Subir logo')}
                    </Button>
                    {hasLogo && (
                      <Button type="button" variant="ghost" onClick={handleLogoDelete} className="text-red-500 hover:text-red-600 text-sm" data-testid="mandante-logo-delete-btn">
                        Quitar logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!editDialog && (
              <div className="space-y-2 border-t border-[#2B4B8C]/10 pt-4">
                <Label className="text-[#2B4B8C]/80">Logo del mandante</Label>
                <p className="text-xs text-[#2B4B8C]/50">Se mostrará en la esquina superior derecha de los certificados. PNG o JPG, máx 5 MB.</p>
                <div className="flex items-center gap-4">
                  <div className="w-36 h-20 rounded-lg border border-dashed border-[#2B4B8C]/20 bg-[#f8fafc] flex items-center justify-center overflow-hidden">
                    {pendingLogoPreview ? (
                      <img src={pendingLogoPreview} alt="Logo mandante" className="max-w-full max-h-full object-contain" data-testid="mandante-logo-preview-new" />
                    ) : (
                      <span className="text-xs text-[#2B4B8C]/30">Sin logo</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      id="mandante-logo-file-new"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = ''; }}
                      data-testid="mandante-logo-input-new"
                    />
                    <Button
                      type="button" variant="outline"
                      onClick={() => document.getElementById('mandante-logo-file-new').click()}
                      className="border-[#2B4B8C]/15 text-[#2B4B8C]"
                      data-testid="mandante-logo-upload-btn-new"
                    >
                      {pendingLogoPreview ? 'Cambiar logo' : 'Subir logo'}
                    </Button>
                    {pendingLogoPreview && (
                      <Button type="button" variant="ghost" onClick={handleLogoDelete} className="text-red-500 hover:text-red-600 text-sm" data-testid="mandante-logo-delete-btn-new">
                        Quitar logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog(null); setCreateDialog(false); }} className="border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={handleSave} disabled={!formData.name.trim()} className="bg-[#2BBAA4] hover:bg-[#2BBAA4]/90 text-white" data-testid="save-mandante-btn">
              {editDialog ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <WarningCircle size={22} weight="fill" /> Eliminar Mandante
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-sm text-[#2B4B8C]/70">
              Se eliminará <strong className="text-[#2B4B8C]">{deleteDialog?.name}</strong> y en cascada:
            </p>
            <ul className="text-sm text-red-600/80 space-y-1 pl-4 list-disc">
              <li>Todos sus cursos configurados</li>
              <li>Capacitaciones vinculadas a esos cursos</li>
              <li>Inscripciones y evaluaciones de esas capacitaciones</li>
              <li>Certificados generados</li>
            </ul>
            <p className="text-xs text-red-500 font-medium mt-2">Esta acción es irreversible.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-mandante">
              {deleting ? 'Eliminando...' : 'Eliminar todo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
